import { llmRequest } from "./client";
import { ENRICHMENT_SYSTEM_PROMPT } from "./prompts";
import type { Profile } from "../fs/profile";
import type { WorkflowGapQuestion } from "../fs/workflows";

export interface AnswerInput {
  question_id: string;
  requirement: string;
  classification: "inferred" | "gap";
  question_en: string;
  answer_text: string;
}

export interface OpenWorkflowInput {
  workflow_id: string;
  vacancy_id: string;
  vacancy_title: string;
  vacancy_requirements: {
    hard: string[];
    preferred: string[];
    competencies: string[];
  };
  current_match_score: string;
  gap_questions: {
    id: string;
    requirement: string;
    classification: "inferred" | "gap";
    status: string;
  }[];
}

export interface SkillToConfirm {
  skill: string;
  evidence: string;
  years_of_experience: number;
  move_from_inferred: boolean;
}

export interface SkillToGap {
  skill: string;
  notes: string;
}

export interface NewInference {
  skill: string;
  confidence: "high" | "medium" | "low";
  inference_basis: string;
}

export interface QuestionResolution {
  question_id: string;
  resolved_as: "confirmed" | "gap" | "unknown";
  notes: string;
}

export interface WorkflowUpdate {
  workflow_id: string;
  new_match_score: "strong" | "good" | "partial" | "weak" | "no_fit";
  score_changed: boolean;
  submittable: boolean;
  remaining_blockers: string[];
}

export interface EnrichmentResult {
  profile_updates: {
    skills_to_confirm: SkillToConfirm[];
    skills_to_gap: SkillToGap[];
    new_inferences: NewInference[];
  };
  question_resolutions: QuestionResolution[];
  workflow_updates: WorkflowUpdate[];
}

const schema = {
  type: "object",
  properties: {
    profile_updates: {
      type: "object",
      properties: {
        skills_to_confirm: {
          type: "array",
          items: {
            type: "object",
            properties: {
              skill: { type: "string" },
              evidence: { type: "string" },
              years_of_experience: { type: "integer" },
              move_from_inferred: { type: "boolean" },
            },
            required: ["skill", "evidence", "years_of_experience", "move_from_inferred"],
          },
        },
        skills_to_gap: {
          type: "array",
          items: {
            type: "object",
            properties: {
              skill: { type: "string" },
              notes: { type: "string" },
            },
            required: ["skill", "notes"],
          },
        },
        new_inferences: {
          type: "array",
          items: {
            type: "object",
            properties: {
              skill: { type: "string" },
              confidence: { type: "string", enum: ["high", "medium", "low"] },
              inference_basis: { type: "string" },
            },
            required: ["skill", "confidence", "inference_basis"],
          },
        },
      },
      required: ["skills_to_confirm", "skills_to_gap", "new_inferences"],
    },
    question_resolutions: {
      type: "array",
      items: {
        type: "object",
        properties: {
          question_id: { type: "string" },
          resolved_as: { type: "string", enum: ["confirmed", "gap", "unknown"] },
          notes: { type: "string" },
        },
        required: ["question_id", "resolved_as", "notes"],
      },
    },
    workflow_updates: {
      type: "array",
      items: {
        type: "object",
        properties: {
          workflow_id: { type: "string" },
          new_match_score: { type: "string", enum: ["strong", "good", "partial", "weak", "no_fit"] },
          score_changed: { type: "boolean" },
          submittable: { type: "boolean" },
          remaining_blockers: { type: "array", items: { type: "string" } },
        },
        required: ["workflow_id", "new_match_score", "score_changed", "submittable", "remaining_blockers"],
      },
    },
  },
  required: ["profile_updates", "question_resolutions", "workflow_updates"],
};

const emailAnswerSchema = {
  type: "object",
  properties: {
    answers: {
      type: "array",
      items: {
        type: "object",
        properties: {
          question_id: { type: "string" },
          answer: { type: "string" },
          resolved_as: { type: "string", enum: ["confirmed", "gap", "unknown"] },
          years_of_experience: { type: "integer" },
        },
        required: ["question_id", "answer", "resolved_as"],
      },
    },
  },
  required: ["answers"],
};

export async function processEnrichmentAnswers(
  _apiKey: string,   // kept for call-site compat during transition; ignored
  _model: string,
  profileJson: string,
  answers: AnswerInput[],
  openWorkflows: OpenWorkflowInput[],
  systemPrompt?: string | null,
): Promise<EnrichmentResult> {
  const userPrompt =
    `Current consultant profile (JSON):\n${profileJson}\n\n` +
    `Answered gap questions:\n${JSON.stringify(answers, null, 2)}\n\n` +
    `Open workflows to re-evaluate:\n${JSON.stringify(openWorkflows, null, 2)}\n\n` +
    `Process the answers, update the profile, and re-evaluate each workflow's match score.`;

  return llmRequest<EnrichmentResult>({
    systemPrompt: systemPrompt || ENRICHMENT_SYSTEM_PROMPT,
    userContent: [{ type: "text", text: userPrompt }],
    schema,
  });
}

export async function parseEmailResponseWithAI(
  _apiKey: string,   // kept for call-site compat during transition; ignored
  _model: string,
  pastedEmail: string,
  gapQuestions: WorkflowGapQuestion[],
): Promise<ParsedEmailAnswer[]> {
  const systemPrompt = `You are an AI assistant designed to extract answers to specific candidate gap questions from a pasted email response.
Given the list of gap questions and the pasted email response from the consultant:
For each question, determine if the consultant answered it. If they did, extract:
1. The extracted answer (summarized cleanly in the same language as the email or English).
2. The resolution: 'confirmed' (if they confirm they have the skill/experience), 'gap' (if they confirm they do NOT have the skill), or 'unknown' (if not mentioned or unclear).
3. If 'confirmed', extract the years of experience if mentioned (default to 1 if not specified).`;

  const userPrompt = `Gap questions:\n${JSON.stringify(gapQuestions, null, 2)}\n\nPasted email response:\n${pastedEmail}\n\nExtract the answers.`;

  const result = await llmRequest<{ answers: ParsedEmailAnswer[] }>({
    systemPrompt,
    userContent: [{ type: "text", text: userPrompt }],
    schema: emailAnswerSchema,
  });
  return result.answers || [];
}

export interface ParsedEmailAnswer {
  question_id: string;
  answer: string;
  resolved_as: "confirmed" | "gap" | "unknown";
  years_of_experience?: number;
}

export function applyProfileUpdates(
  profile: Profile,
  updates: EnrichmentResult["profile_updates"],
  now: string,
): Profile {
  const result = {
    ...profile,
    confirmed_skills: [...profile.confirmed_skills],
    inferred_skills: [...profile.inferred_skills],
    confirmed_gaps: [...profile.confirmed_gaps],
    last_enriched_at: now,
  };

  for (const s of updates.skills_to_confirm) {
    if (s.move_from_inferred) {
      result.inferred_skills = result.inferred_skills.filter(
        (i: any) => i.skill.toLowerCase() !== s.skill.toLowerCase(),
      );
    }
    result.confirmed_skills = result.confirmed_skills.filter(
      (c: any) => c.skill.toLowerCase() !== s.skill.toLowerCase(),
    );
    result.confirmed_gaps = result.confirmed_gaps.filter(
      (g: any) => g.skill.toLowerCase() !== s.skill.toLowerCase(),
    );
    result.confirmed_skills.push({
      skill: s.skill,
      years: s.years_of_experience,
      evidence: s.evidence,
      source: "enrichment",
      confirmed_at: now,
    });
  }

  for (const g of updates.skills_to_gap) {
    result.confirmed_skills = result.confirmed_skills.filter(
      (c: any) => c.skill.toLowerCase() !== g.skill.toLowerCase(),
    );
    result.inferred_skills = result.inferred_skills.filter(
      (i: any) => i.skill.toLowerCase() !== g.skill.toLowerCase(),
    );
    result.confirmed_gaps = result.confirmed_gaps.filter(
      (eg: any) => eg.skill.toLowerCase() !== g.skill.toLowerCase(),
    );
    result.confirmed_gaps.push({ skill: g.skill, notes: g.notes, confirmed_at: now });
  }

  for (const inf of updates.new_inferences) {
    const alreadyKnown =
      result.confirmed_skills.some((c: any) => c.skill.toLowerCase() === inf.skill.toLowerCase()) ||
      result.inferred_skills.some((i: any) => i.skill.toLowerCase() === inf.skill.toLowerCase());
    if (!alreadyKnown) {
      result.inferred_skills.push({
        skill: inf.skill,
        confidence: inf.confidence,
        inference_basis: inf.inference_basis,
        confirmed: false,
        confirmed_at: null,
      });
    }
  }

  return result;
}
